<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { X } from 'lucide-vue-next'
import type { DemoVideo } from '@/data/videoDemo'
import { getVideoHomeCopy, type HomeLocale } from '@/data/videoHomeCopy'
import DemoVideoPlayer from './DemoVideo.vue'

const props = withDefaults(defineProps<{ video: DemoVideo | null; locale?: HomeLocale }>(), { locale: 'zh' })
const emit = defineEmits<{ close: [] }>()
const copy = computed(() => getVideoHomeCopy(props.locale).video)
const closeButton = ref<HTMLButtonElement | null>(null)
let previousFocus: HTMLElement | null = null

const close = () => emit('close')
const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.video) close()
}

watch(() => props.video, async (value) => {
  document.body.style.overflow = value ? 'hidden' : ''
  if (value) {
    previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    await nextTick()
    closeButton.value?.focus()
  } else {
    previousFocus?.focus()
    previousFocus = null
  }
}, { immediate: true })

window.addEventListener('keydown', onKeydown)
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  document.body.style.overflow = ''
})
</script>

<template>
  <Teleport to="body">
    <div v-if="video" class="lightbox-backdrop" role="presentation" @click.self="close">
      <section class="lightbox-card" role="dialog" aria-modal="true" :aria-label="video.title">
        <header>
          <div>
            <strong>{{ video.title }}</strong>
            <span>{{ video.type }} · {{ video.duration }} · {{ video.ratio }}</span>
          </div>
          <div class="lightbox-actions">
            <button ref="closeButton" type="button" :aria-label="copy.closePlayer" @click="close"><X :size="22" aria-hidden="true" /></button>
          </div>
        </header>
        <div class="lightbox-video-shell">
          <DemoVideoPlayer :video="video" :locale="locale" mode="hero" autoplay :loop="false" />
        </div>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.lightbox-backdrop {
  position: fixed;
  z-index: 200;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 24px;
  color: #fff;
  background: rgba(0, 4, 8, .86);
  backdrop-filter: blur(18px);
}
.lightbox-card {
  width: min(1120px, 100%);
  max-height: calc(100svh - 48px);
  overflow: auto;
  border: 1px solid rgba(255,255,255,.13);
  border-radius: 22px;
  background: #080d12;
  box-shadow: 0 45px 120px rgba(0,0,0,.55);
}
header { padding: 18px 20px; display: flex; align-items: center; justify-content: space-between; gap: 22px; }
header strong { display: block; margin-bottom: 4px; font-size: 17px; }
header span { color: rgba(255,255,255,.52); font-size: 12px; }
.lightbox-actions { display: flex; gap: 7px; }
button {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  color: #fff;
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 50%;
  background: rgba(255,255,255,.06);
}
button:hover { background: rgba(255,255,255,.13); }
.lightbox-video-shell { width: 100%; aspect-ratio: 16 / 9; max-height: min(68svh, 720px); background: #000; }
@media (max-width: 640px) {
  .lightbox-backdrop { padding: 0; }
  .lightbox-card { width: 100%; max-height: 100svh; border: 0; border-radius: 0; }
  header { padding: 14px 15px; }
  footer { display: grid; }
  .lightbox-video-shell { max-height: 70svh; }
}
</style>
